/**
 * Module-level registry of the active DataSource.
 *
 * The shell installs a DataSource at startup (LocalDataSource for Electron,
 * RemoteDataSource for the web shell). All callers reach the active instance
 * through getDataSource().
 *
 * Why a module-level singleton instead of React context:
 *   - Most callers are non-React utility functions (e.g. helpers in lib/)
 *   - The DataSource is a process-level resource, not component-tree state
 *   - The shell installs it before React mounts
 *   - This matches the previous app/src/lib/ipc.ts pattern (functions, not hooks)
 *
 * Components can still wrap getDataSource() in a hook if they want to react
 * to data-source swaps (rare — typically only happens during tests).
 */

import type { DataSource } from './types.js';

let active: DataSource | null = null;

export function setDataSource(ds: DataSource): void {
  active = ds;
}

export function getDataSource(): DataSource {
  if (!active) {
    throw new Error(
      '[@cipherops/data-source] No DataSource installed. ' +
        'The shell must call setDataSource(new LocalDataSource()) ' +
        'or setDataSource(new RemoteDataSource(...)) before any UI mounts.',
    );
  }
  return active;
}
